import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Utensils, Plus, X, ChevronLeft, ChevronRight, Phone, Trash2, Edit3, Users } from "lucide-react";

const STATUS = {
  booked: { bg: "#3B82F6", text: "Prenotato" },
  seated: { bg: "#F59E0B", text: "Al tavolo" },
  completed: { bg: "#2F9E77", text: "Concluso" },
  cancelled: { bg: "#94A3B8", text: "Annullato" },
  no_show: { bg: "#DC2626", text: "No-show" },
};
const todayISO = () => new Date().toISOString().slice(0, 10);
const addDays = (iso, n) => {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};
const fmtDay = (iso) => {
  try {
    return new Date(`${iso}T12:00:00`).toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long" });
  } catch { return iso; }
};

export default function RestaurantReservations() {
  const [date, setDate] = useState(todayISO());
  const [items, setItems] = useState([]);
  const [tables, setTables] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    customer_name: "", customer_phone: "", covers: 2, date: todayISO(), time: "20:00",
    table_id: "", notes: "", status: "booked",
  });

  const load = () => api.get(`/reservations?date=${date}`).then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => setItems([]));
  useEffect(() => {
    api.get("/tables").then((r) => setTables(Array.isArray(r.data) ? r.data : [])).catch(() => setTables([]));
  }, []);
  useEffect(() => { load(); }, [date]);

  const openNew = () => {
    setEditing(null);
    setForm({ customer_name: "", customer_phone: "", covers: 2, date, time: "20:00", table_id: "", notes: "", status: "booked" });
    setShowForm(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      customer_name: r.customer_name || "",
      customer_phone: r.customer_phone || "",
      covers: r.covers || 2,
      date: r.date || date,
      time: r.time || "20:00",
      table_id: r.table_id || "",
      notes: r.notes || "",
      status: r.status || "booked",
    });
    setShowForm(true);
  };
  const save = async () => {
    if (!form.customer_name.trim()) return toast.error("Nome obbligatorio");
    try {
      const payload = { ...form, table_id: form.table_id || null, covers: parseInt(form.covers, 10) || 1 };
      if (editing) await api.patch(`/reservations/${editing.id}`, payload);
      else await api.post("/reservations", payload);
      toast.success(editing ? "Prenotazione aggiornata" : "Prenotazione creata");
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };
  const setStatus = async (r, status) => {
    try {
      await api.patch(`/reservations/${r.id}/status?status=${status}`);
      load();
    } catch { toast.error("Errore"); }
  };
  const del = async (r) => {
    if (!window.confirm(`Eliminare la prenotazione di ${r.customer_name}?`)) return;
    try {
      await api.delete(`/reservations/${r.id}`);
      toast.success("Eliminata");
      load();
    } catch { toast.error("Errore"); }
  };

  const counts = useMemo(() => ({
    booked: items.filter((x) => x.status === "booked").length,
    covers: items.filter((x) => x.status !== "cancelled" && x.status !== "no_show").reduce((s, x) => s + (x.covers || 0), 0),
  }), [items]);

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-velta-mute mb-2">Sala</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight flex items-center gap-3">
            <Utensils className="w-8 h-8" /> Prenotazioni
          </h1>
          <p className="text-velta-mute mt-1">Coperti e tavolo per il servizio. La cassa / comande restano su Apri Cassa.</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setDate(addDays(date, -1))} className="h-10 w-10 border rounded-md flex items-center justify-center"><ChevronLeft className="w-4 h-4" /></button>
          <input data-testid="resv-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-10 px-3 border rounded-md text-sm" />
          <button type="button" onClick={() => setDate(addDays(date, 1))} className="h-10 w-10 border rounded-md flex items-center justify-center"><ChevronRight className="w-4 h-4" /></button>
          <button type="button" onClick={() => setDate(todayISO())} className="h-10 px-3 border rounded-md text-xs">Oggi</button>
          <button data-testid="resv-new" type="button" onClick={openNew} className="h-10 px-4 bg-velta-ink text-white rounded-md text-sm inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Nuova prenotazione</button>
        </div>
      </div>
      <div className="mb-4 text-sm capitalize">{fmtDay(date)} · {counts.booked} confermate · {counts.covers} coperti</div>

      <div className="bg-white border border-velta-border rounded-md overflow-x-auto pos-scroll">
        <table className="w-full text-sm min-w-[860px]">
          <thead className="bg-velta-bg text-left text-xs font-mono uppercase tracking-widest text-velta-mute">
            <tr>
              <th className="px-4 py-3">Ora</th>
              <th className="px-4 py-3">Cliente</th>
              <th className="px-4 py-3">Coperti</th>
              <th className="px-4 py-3">Tavolo</th>
              <th className="px-4 py-3">Note / allergie</th>
              <th className="px-4 py-3">Stato</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody data-testid="resv-tbody">
            {items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-velta-mute">Nessuna prenotazione in questo giorno.</td></tr>
            )}
            {items.map((r) => {
              const s = STATUS[r.status] || STATUS.booked;
              return (
                <tr key={r.id} className="border-t border-velta-border">
                  <td className="px-4 py-3 font-mono">{r.time}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{r.customer_name}</div>
                    {r.customer_phone && <div className="text-[11px] text-velta-mute flex items-center gap-1"><Phone className="w-3 h-3" /> {r.customer_phone}</div>}
                  </td>
                  <td className="px-4 py-3"><span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {r.covers}</span></td>
                  <td className="px-4 py-3">{r.table_name || "—"}</td>
                  <td className="px-4 py-3 text-velta-mute text-xs max-w-xs">{r.notes || "—"}</td>
                  <td className="px-4 py-3">
                    <span className="text-[10px] font-mono uppercase text-white px-2 py-0.5 rounded" style={{ background: s.bg }}>{s.text}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      {r.status === "booked" && (
                        <button type="button" className="text-xs px-2 h-8 border rounded-md" onClick={() => setStatus(r, "seated")}>Al tavolo</button>
                      )}
                      {r.status === "seated" && (
                        <button type="button" className="text-xs px-2 h-8 border rounded-md" onClick={() => setStatus(r, "completed")}>Concluso</button>
                      )}
                      <button type="button" onClick={() => openEdit(r)} className="h-8 w-8 rounded hover:bg-velta-bg flex items-center justify-center"><Edit3 className="w-3.5 h-3.5" /></button>
                      <button type="button" onClick={() => del(r)} className="h-8 w-8 rounded hover:bg-red-50 text-velta-error flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-md w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between mb-4">
              <h3 className="font-heading text-2xl font-semibold">{editing ? "Modifica prenotazione" : "Nuova prenotazione"}</h3>
              <button type="button" onClick={() => setShowForm(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <input data-testid="resv-form-name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder="Nome *" className="w-full h-11 px-3 border rounded-md" />
              <input data-testid="resv-form-phone" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} placeholder="Telefono" className="w-full h-11 px-3 border rounded-md font-mono" />
              <div className="grid grid-cols-3 gap-2">
                <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="h-11 px-3 border rounded-md" />
                <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="h-11 px-3 border rounded-md" />
                <input data-testid="resv-form-covers" type="number" min={1} value={form.covers} onChange={(e) => setForm({ ...form, covers: e.target.value })} className="h-11 px-3 border rounded-md" title="Coperti" />
              </div>
              <select value={form.table_id} onChange={(e) => setForm({ ...form, table_id: e.target.value })} className="w-full h-11 px-3 border rounded-md bg-white">
                <option value="">Senza tavolo (assegnalo al servizio)</option>
                {tables.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}{t.room ? ` · ${t.room}` : ""} · {t.seats} posti</option>
                ))}
              </select>
              <textarea data-testid="resv-form-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Allergie, celiaco, compleanno, preferenze…" className="w-full p-3 border rounded-md text-sm" />
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 h-11 border rounded-md">Annulla</button>
                <button type="button" data-testid="resv-form-save" onClick={save} className="flex-1 h-11 bg-velta-ink text-white rounded-md">Salva</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
